import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import type { ApiEndpoint, EntityItem, MediaItem } from "../types";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

// Callbacks that can be provided when opening forms for custom behavior
export interface EntityFormCallbacks {
  onUpdated?: (item: any) => Promise<void> | void;
  onDeleted?: (id: number | string) => Promise<void> | void;
  onCancel?: (item?: any) => void;
}

interface EntityFormState {
  open: boolean;
  mode: "create" | "edit";
  endpoint: ApiEndpoint | null;
  existingItem?: MediaItem | EntityItem;
  callbacks?: EntityFormCallbacks;

  // Separate namespace for store actions
  actions: {
    openCreateForm: (endpoint: ApiEndpoint, callbacks?: EntityFormCallbacks) => void;
    openEditForm: (endpoint: ApiEndpoint, item: MediaItem | EntityItem, callbacks?: EntityFormCallbacks) => void;
    closeForm: () => void;
    setFormState: (payload: Partial<Omit<EntityFormState, "actions">>) => void;
    resetFormState: () => void;
  };
}

const actionName = actionNameBuilder("entityForm");

export const useEntityFormStore = create<EntityFormState>()(
  devtools(
    immer(
      applyResetMiddleware((set, _, store) => ({
        open: false,
        mode: "create",
        endpoint: null,
        existingItem: undefined,
        callbacks: undefined,

        actions: {
          openCreateForm: (endpoint, callbacks) =>
            set(
              () => ({
                open: true,
                mode: "create",
                endpoint,
                existingItem: undefined,
                callbacks
              }),
              undefined,
              actionName("openCreateForm")
            ),

          openEditForm: (endpoint, item, callbacks) =>
            set(
              () => ({
                open: true,
                mode: "edit",
                endpoint,
                existingItem: item,
                callbacks
              }),
              undefined,
              actionName("openEditForm")
            ),

          closeForm: () =>
            set(
              (state) => {
                state.open = false;
              },
              undefined,
              actionName("closeForm")
            ),

          setFormState: (payload) =>
            set(
              (state) => {
                Object.assign(state, payload);
              },
              undefined,
              actionName("setFormState")
            ),

          resetFormState: () => set(store.getInitialState(), undefined, actionName("resetFormState"))
        }
      }))
    ),
    {
      name: "entityFormStore"
    }
  )
);

// Hook for store states
export const useEntityFormSelector = () =>
  useEntityFormStore(
    useShallow((state) => ({
      open: state.open,
      mode: state.mode,
      endpoint: state.endpoint,
      existingItem: state.existingItem,
      callbacks: state.callbacks
    }))
  );

// Hook for store actions
export const useEntityFormActions = () => useEntityFormStore((state) => state.actions);
